import { addMonths, format, startOfMonth } from 'date-fns'
import { FC, useMemo, useState } from 'react'

import { canCheckIn, canCheckOut, indexCalendar } from './dateRangeSelection'
import { MonthGrid } from './MonthGrid'
import { CalendarDay, HighlightedRange } from './types'
import * as S from './AvailabilityCalendar.styles'

const MONTH_LABEL = 'MMMM yyyy'

export interface DateRangeValue {
  checkInDate?: string
  checkOutDate?: string
}

export interface AvailabilityCalendarProps {
  // Omit to allow every future date; supply it to constrain selection to real availability.
  days?: CalendarDay[]
  minDate?: string
  maxDate?: string
  // One click selects one day; there is no stay to complete.
  singleDate?: boolean
  // Opt in to 'any future date'. Never inferred from an empty `days`.
  allowAnyFutureDate?: boolean
  highlightedRange?: HighlightedRange
  value?: DateRangeValue
  defaultValue?: DateRangeValue
  initialMonth?: Date
  onChange?: (range: DateRangeValue) => void
}

export const AvailabilityCalendar: FC<AvailabilityCalendarProps> = ({
  days = [],
  minDate,
  maxDate,
  singleDate = false,
  allowAnyFutureDate = false,
  highlightedRange,
  value,
  defaultValue,
  initialMonth,
  onChange,
}) => {
  const calendar = useMemo(
    () => indexCalendar(days, { minDate, maxDate }, allowAnyFutureDate),
    [days, minDate, maxDate, allowAnyFutureDate],
  )
  const [internalRange, setInternalRange] = useState<DateRangeValue>(value ?? defaultValue ?? {})
  const [hoveredDate, setHoveredDate] = useState<string>()
  const [firstMonth, setFirstMonth] = useState<Date>(() => startOfMonth(initialMonth ?? new Date()))

  const range = value ?? internalRange
  const secondMonth = addMonths(firstMonth, 1)

  const applyRange = (next: DateRangeValue) => {
    if (!value) setInternalRange(next)
    onChange?.(next)
  }

  // First click sets the check-in; a second valid click completes the stay; anything else restarts
  // from the clicked day when it can be a check-in.
  const handleSelect = (date: string) => {
    if (singleDate) {
      if (canCheckIn(calendar, date)) applyRange({ checkInDate: date, checkOutDate: date })
      return
    }

    const { checkInDate, checkOutDate } = range
    if (checkInDate && !checkOutDate && canCheckOut(calendar, checkInDate, date)) {
      applyRange({ checkInDate, checkOutDate: date })
      return
    }
    if (canCheckIn(calendar, date)) {
      applyRange({ checkInDate: date })
    }
  }

  const renderMonth = (monthDate: Date) => (
    <MonthGrid
      monthDate={monthDate}
      calendar={calendar}
      checkInDate={range.checkInDate}
      checkOutDate={range.checkOutDate}
      hoveredDate={hoveredDate}
      highlightedRange={highlightedRange}
      onSelectDate={handleSelect}
      onHoverDate={setHoveredDate}
    />
  )

  return (
    <S.Calendar>
      <S.Body>
        <S.Column>
          <S.ColumnHeader>
            <S.NavButton type="button" onClick={() => setFirstMonth(addMonths(firstMonth, -1))}>
              ‹
            </S.NavButton>
            <S.MonthLabel>{format(firstMonth, MONTH_LABEL)}</S.MonthLabel>
            <S.Spacer />
          </S.ColumnHeader>
          {renderMonth(firstMonth)}
        </S.Column>
        <S.Column>
          <S.ColumnHeader>
            <S.Spacer />
            <S.MonthLabel>{format(secondMonth, MONTH_LABEL)}</S.MonthLabel>
            <S.NavButton type="button" onClick={() => setFirstMonth(addMonths(firstMonth, 1))}>
              ›
            </S.NavButton>
          </S.ColumnHeader>
          {renderMonth(secondMonth)}
        </S.Column>
      </S.Body>
    </S.Calendar>
  )
}
